/**
 * Pure auto-ID product-number generation for the table view.
 *
 * Ported verbatim from the CreateProduct page's `handleAutoGenerateProductNumber`
 * (react_app/src/Pages/DevlopmentOrder/ProductInformation/CreateProduct/index.tsx),
 * with its redux/component reads lifted into an explicit `context` bundle so the
 * function is pure and testable. CreateProduct is intentionally NOT refactored to use
 * this — the two must stay behaviourally identical, and any change here must be mirrored
 * there and in `Components/ProductInformation/ProductModal.js`.
 */

/** A selected child property is either a raw key string or an option-shaped object. */
export type AutoIdProperty = string | { value?: string; [key: string]: unknown };

/** Resolved auto-id config for one scope (universal or brand-specific). */
export interface AutoIdConfig {
  isActive: boolean;
  counterNo: string;
  selectedChildProperties: AutoIdProperty[];
}

/** Reference-library entry keyed by `_id` with a display alias. */
export interface AliasOption {
  _id?: string | { toString(): string };
  aliasName?: string;
  name?: string;
}

/** Colourway library entry — carries the reference code used in the number. */
export interface ColourwayOption {
  _id?: string | { toString(): string };
  newReferences?: string;
}

/** A single selectable value inside a multi-custom attribute. */
export interface MultiCustomAttributeValue {
  _id?: string | { toString(): string };
  aliasName?: string | null;
}

/** A multi-custom attribute definition with its selectable values. */
export interface MultiCustomAttribute {
  _id?: string | { toString(): string };
  values?: MultiCustomAttributeValue[];
}

/** A dynamic column value carried on the row. */
export interface DynamicColumnValue {
  _id?: string | { toString(): string };
  selectedOpt?: { aliasName?: string };
}

/** The selected multi-custom attribute entry on a row (colId → valId list). */
export interface RowMultiCustomAttribute {
  colId?: string | { toString(): string };
  valId?: Array<string | { toString(): string }>;
}

/** A product type as carried on the row — either a raw id or a populated reference. */
export type RowProductType = string | { _id?: string | { toString(): string }; aliasName?: string; name?: string };

/** The subset of the product row the generator reads. */
export interface AutoIdRow {
  brandId?: string | { toString(): string };
  subBrandId?: string | { toString(): string };
  supplierType?: RowProductType;
  /** The counter already stored on a saved product — reused instead of burning a fresh one. */
  counterNo?: string;
  seasonId?: string | { toString(): string };
  month?: string | { toString(): string };
  year?: string | { toString(): string };
  catOneId?: string | { toString(): string };
  catTwoId?: string | { toString(): string };
  catThreeId?: string | { toString(): string };
  catFourId?: string | { toString(): string };
  colourLibraries?: Array<string | { toString(): string }>;
  dynamicColumn?: DynamicColumnValue[];
  multiCustomAttribute?: RowMultiCustomAttribute[];
}

/** Everything the generator reads, assembled from redux by the caller. */
export interface AutoIdContext {
  universal: AutoIdConfig;
  brand: AutoIdConfig;
  row: AutoIdRow;
  brandList: AliasOption[];
  subBrandList: AliasOption[];
  seasonList: AliasOption[];
  monthOptions: AliasOption[];
  yearOptions: AliasOption[];
  category1List: AliasOption[];
  category2List: AliasOption[];
  category3List: AliasOption[];
  category4List: AliasOption[];
  category3Options?: AliasOption[] | null;
  category4Options?: AliasOption[] | null;
  productTypeList: AliasOption[];
  colorWaysLibList: ColourwayOption[];
  multiDynamicAttribute: MultiCustomAttribute[];
  /**
   * True when the row is an already-saved product (not the add draft). Such a row keeps the
   * counter it was created with rather than consuming a new one on every re-generate.
   */
  isExistingProduct?: boolean;
  /** Async fetch of the current counter for the given brand (dispatch(getCounterValueNumber)). */
  getCounterValue: (brandId: AutoIdRow['brandId']) => Promise<string>;
}

export interface AutoIdResult {
  number: string;
  counterNo: string;
}

/**
 * Compute the auto-generated product number from the row's selected properties.
 * @param context redux-derived inputs + the counter fetch callback
 * @returns the generated number + counter, or null when nothing could be generated
 *          (e.g. brand-scoped config with no brand selected, or all parts empty)
 */
export const generateAutoProductNumber = async (context: AutoIdContext): Promise<AutoIdResult | null> => {
  const {
    universal,
    brand: brandConfig,
    row,
    brandList,
    subBrandList,
    seasonList,
    monthOptions,
    yearOptions,
    category1List,
    category2List,
    category3List,
    category4List,
    category3Options,
    category4Options,
    productTypeList,
    colorWaysLibList,
    multiDynamicAttribute,
    isExistingProduct,
    getCounterValue,
  } = context;

  if (brandConfig?.isActive && !row?.brandId) {
    return null;
  }

  const propertiesToGenerateAutoId =
    universal?.selectedChildProperties?.length > 0
      ? universal?.selectedChildProperties
      : brandConfig?.selectedChildProperties?.length > 0
      ? brandConfig?.selectedChildProperties
      : [];

  let selectedCounterType: string | null = null;
  if (propertiesToGenerateAutoId?.length > 0) {
    const propertiesArray = propertiesToGenerateAutoId.map((prop) => (typeof prop === 'object' && prop?.value ? prop.value : prop));
    if (propertiesArray.includes('3')) {
      selectedCounterType = '3';
    } else if (propertiesArray.includes('5') || propertiesArray.includes('counter')) {
      selectedCounterType = '5';
    }
  }
  const defaultCounterValue = selectedCounterType === '3' ? '000' : '00000';

  let getCurrentCounterNo = '';
  if (propertiesToGenerateAutoId?.length > 0 && (universal?.isActive || brandConfig?.isActive)) {
    if (isExistingProduct && row?.counterNo && row.counterNo !== defaultCounterValue) {
      getCurrentCounterNo = row.counterNo;
    } else {
      getCurrentCounterNo = await getCounterValue(row?.brandId);
    }
  }

  const brandMap = new Map(brandList?.map((brandOption) => [brandOption._id?.toString(), brandOption.aliasName]) || []);
  const subBrandMap = new Map(subBrandList?.map((subBrandOption) => [subBrandOption._id?.toString(), subBrandOption.aliasName]) || []);
  const seasonMap = new Map(seasonList?.map((seasonOption) => [seasonOption._id?.toString(), seasonOption.aliasName]) || []);
  const monthMap = new Map(monthOptions?.map((monthOption) => [monthOption._id?.toString(), monthOption.name]) || []);
  const yearMap = new Map(yearOptions?.map((yearOption) => [yearOption._id?.toString(), yearOption.name]) || []);
  const cat1Map = new Map(category1List?.map((category) => [category._id?.toString(), category.aliasName]) || []);
  const cat2Map = new Map(category2List?.map((category) => [category._id?.toString(), category.aliasName]) || []);
  const cat3Map = new Map((category3Options || category3List)?.map((category) => [category._id?.toString(), category.aliasName]) || []);
  const cat4Map = new Map((category4Options || category4List)?.map((category) => [category._id?.toString(), category.aliasName]) || []);
  const productTypeMap = new Map(
    productTypeList?.map((productTypeOption) => [productTypeOption._id?.toString(), productTypeOption.aliasName || productTypeOption.name]) || [],
  );
  const colorWayMap = new Map(colorWaysLibList?.map((colourway) => [colourway._id?.toString(), colourway]) || []);
  const dynamicFieldsMap = new Map(row?.dynamicColumn?.map((dynamicField) => [dynamicField._id?.toString(), dynamicField]) || []);
  const multiCustomAttributeMap = new Map(multiDynamicAttribute?.map((attribute) => [attribute._id?.toString(), attribute]) || []);
  const multiCustomAttributeSelectedMap = new Map(row?.multiCustomAttribute?.map((attribute) => [attribute.colId?.toString(), attribute.valId]) || []);

  const resolveProperty = (rawProp: AutoIdProperty): string | null => {
    const prop: string | undefined = typeof rawProp === 'object' ? rawProp?.value : rawProp;
    if (prop === undefined) {
      return null;
    }
    if (prop === '-') return '-';
    if (['3', '5', 'counter'].includes(prop)) return getCurrentCounterNo || null;
    const propertyResolvers: Record<string, () => string | null> = {
      brand: () => brandMap.get(row?.brandId?.toString()) ?? null,
      subBrand: () => subBrandMap.get(row?.subBrandId?.toString()) ?? null,
      season: () => seasonMap.get(row?.seasonId?.toString()) ?? null,
      month: () => monthMap.get(row?.month?.toString()) ?? null,
      year: () => yearMap.get(row?.year?.toString()) ?? null,
      category1: () => cat1Map.get(row?.catOneId?.toString()) ?? null,
      category2: () => cat2Map.get(row?.catTwoId?.toString()) ?? null,
      category3: () => cat3Map.get(row?.catThreeId?.toString()) ?? null,
      category4: () => cat4Map.get(row?.catFourId?.toString()) ?? null,
      productType: () => {
        const supplierType = row?.supplierType;
        if (supplierType && typeof supplierType === 'object') {
          return supplierType.aliasName || supplierType.name || productTypeMap.get(supplierType._id?.toString()) || null;
        }
        return productTypeMap.get(supplierType?.toString()) ?? null;
      },
      colourway: () => {
        const selectedColorways = row?.colourLibraries || [];
        if (selectedColorways.length === 1) {
          const colorway = colorWayMap.get(selectedColorways[0]?.toString());
          return colorway?.newReferences || null;
        }
        return null;
      },
    };

    if (propertyResolvers[prop]) {
      return propertyResolvers[prop]();
    }

    const dynamicField = dynamicFieldsMap.get(prop?.toString());
    if (dynamicField?.selectedOpt?.aliasName) {
      return dynamicField?.selectedOpt?.aliasName;
    }
    if (dynamicField && !dynamicField.selectedOpt?.aliasName) {
      return null;
    }

    const multiCustomAttr = multiCustomAttributeMap.get(prop?.toString());
    if (multiCustomAttr) {
      const selectedValIds = multiCustomAttributeSelectedMap.get(prop?.toString());
      if (!selectedValIds || selectedValIds.length === 0) {
        return null;
      }
      const aliasNames = selectedValIds
        .map((valId) => {
          const value = multiCustomAttr.values?.find((candidate) => candidate._id?.toString() === valId?.toString());
          return value?.aliasName !== undefined && value?.aliasName !== null ? value.aliasName : null;
        })
        .filter((nameVal): nameVal is string => nameVal !== null && nameVal !== '');
      return aliasNames.length > 0 ? aliasNames.join('') : null;
    }
    return null;
  };

  const resolvedParts: Array<{ value: string | null; isDash: boolean }> = propertiesToGenerateAutoId?.map((prop) => ({
    value: resolveProperty(prop),
    isDash: prop === '-',
  }));
  const parts: string[] = [];
  for (let i = 0; i < resolvedParts.length; i++) {
    const current = resolvedParts[i];
    if (!current) continue;
    if (current.isDash) {
      const hasPreviousValue = parts.length > 0;
      const hasNextValue = resolvedParts.slice(i + 1).some((part) => part.value && !part.isDash);
      if (hasPreviousValue && hasNextValue) {
        parts.push('-');
      }
    } else if (current.value) {
      parts.push(current.value);
    }
  }

  const generatedNumber = parts.join('');
  if (!generatedNumber) {
    return null;
  }
  return { number: generatedNumber, counterNo: getCurrentCounterNo };
};

/**
 * The counter a new draft should use, given the counters other open drafts already hold.
 *
 * `get-next-counter` only reads the stored counter — it reserves nothing — so every draft that asks
 * before any of them is saved gets the same value. Moving past the highest counter already taken
 * keeps the drafts' numbers distinct, at the fetched value's width. The server's uniqueness check on
 * save stays the final guard (another user can still take a number first).
 * @param fetched the counter the server returned
 * @param taken counters already held by the other open drafts in the same counter scope
 * @returns the fetched counter, or the next one above every taken counter
 */
export const nextFreeCounter = (fetched: unknown, taken: unknown[]): string => {
  const base = String(fetched ?? '');
  if (!/^\d+$/.test(base)) return base;
  const highestTaken = taken
    .map((counter) => String(counter ?? ''))
    .filter((counter) => /^\d+$/.test(counter))
    .reduce((highest, counter) => Math.max(highest, Number(counter)), -1);
  const next = Math.max(Number(base), highestTaken + 1);
  return String(next).padStart(base.length, '0');
};
